import React, { useState } from "react";

// Custom hook for image upload logic
export function useImageUpload(
  maxFileSizeBytes: number,
  cb: (file: File | null) => void,
) {
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [invalidImage, setInvalidImage] = useState<{
    name: string;
    size: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const addImage = (file: File) => {
    // Clear any previous invalid image state first
    setInvalidImage(null);
    setImage(null);
    setImagePreview(null);

    // Validar tipo de archivo
    if (
      !file.type.startsWith("image/jpeg") &&
      !file.type.startsWith("image/jpg")
    ) {
      setInvalidImage({ name: file.name, size: file.size });
      cb(null); // Clear the form value for invalid files
      return;
    }

    // Validar tamaño
    if (file.size > maxFileSizeBytes) {
      setInvalidImage({ name: file.name, size: file.size });
      cb(null); // Clear the form value for invalid files
      return;
    }

    setImage(file);
    cb(file);

    // Crear preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === "string") {
        setImagePreview(result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      addImage(file);
    }
    // Clear the input value to allow re-selecting the same file
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      addImage(file);
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
    setInvalidImage(null);
    cb(null);
  };

  return {
    image,
    setImage,
    imagePreview,
    setImagePreview,
    invalidImage,
    setInvalidImage,
    isDragging,
    setIsDragging,
    handleImageChange,
    handleDrop,
    handleRemoveImage,
  };
}
